// eslint-disable-next-line no-unused-vars
import React, { memo, useCallback, useState, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import omit from 'lodash/omit';

import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import useToastNotifications from 'Hooks/useToastNotifications';
import RegistrationModalComponent from 'Components/Registration/RegistrationModal';
import { API_COUNTRIES, REGISTRATION, EMAIL_REGEX } from 'Constants';

const COUNTIES = 'counties';
const CITIES = 'cities';
const ROMANIA_ID = 192;
const languages = [
  { label: 'RO', value: 'ro' },
  { label: 'EN', value: 'en' }
];

const defaultValues = {
  companyName: '',
  companyActivity: '',
  companyTaxIdentificationNumber: '',
  companyCountry: null,
  companyCounty: null,
  companyCountyText: '',
  companyCityText: '',
  companyCity: null,
  companyAddress: '',
  companyPhone: '',
  companyFax: '',
  contactFirstName: '',
  contactLastName: '',
  contactPosition: '',
  contactEmail: '',
  // cspAccount: false,
  // mpnId: '',
  gdprConsent: false,
  captcha: false,
};

const RegistrationModal = memo(() => {
  const { t } = useTranslation();
  const fetch = useFetch();
  const toast = useToastNotifications();
  const { data: user, setUserData } = useUser();
  const recaptchaRef = useRef(null);
  const modalRef = useRef(null);
  const {
    formState: { errors: rhfErrors },
    watch,
    register,
    setValue,
    getValues,
    trigger,
    reset,
    control,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    shouldFocusError: true,
  });

  const [companyCountries, setCompanyCountries] = useState(null);
  const [companyCounties, setCompanyCounties] = useState(null);
  const [companyCities, setCompanyCities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const companyCountryValue = watch('companyCountry');
  const companyCountyValue = watch('companyCounty');
  const companyCityValue = watch('companyCity');
  const cspAccountValue = watch('cspAccount');

  const required = t('errors.fieldBlank');
  const minLength = value => ({ value, message: t('errors.fieldMinLength', { minLength: value }) });
  const maxLength = value => ({ value, message: t('errors.fieldMaxLength', { maxLength: value }) });
  const pattern = value => ({ value, message: t('errors.filedInvalidFormat') });

  const validators = {
    companyName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(50),
    },
    companyActivity: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(50),
    },
    companyTaxIdentificationNumber: {
      required,
      pattern: pattern(/^(RO)?\d{6,10}$/),
    },
    companyCountry: {
      required,
    },
    companyCounty: {
      required: companyCountryValue === ROMANIA_ID ? t('errors.fieldBlank') : false,
    },
    companyCity: {
      required: companyCountryValue === ROMANIA_ID ? t('errors.fieldBlank') : false,
    },
    companyCountyText: {
      required: companyCountryValue !== ROMANIA_ID ? required : false,
      minLength: companyCountryValue !== ROMANIA_ID ? minLength(3) : false,
      maxLength: companyCountryValue !== ROMANIA_ID ? maxLength(50) : false,
    },
    companyCityText: {
      required: companyCountryValue !== ROMANIA_ID ? required : false,
      minLength: companyCountryValue !== ROMANIA_ID ? minLength(3) : false,
      maxLength: companyCountryValue !== ROMANIA_ID ? maxLength(50) : false,
    },
    companyAddress: {
      required,
      minLength: minLength(10),
      maxLength: maxLength(120),
    },
    companyPhone: {
      required,
      pattern: pattern(/^(\+|00)?\d{9,13}$/),
    },
    companyFax: {
      pattern: pattern(/^(\+|00)?\d{9,13}$/),
    },
    contactFirstName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(50),
    },
    contactLastName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(50),
    },
    contactPosition: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(50),
    },
    contactEmail: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(50),
      pattern: {
        value: EMAIL_REGEX,
        message: t('errors.invalidEmail'),
      },
    },
    cspAccount: {},
    mpnId: {
      required: cspAccountValue ? required : false,
      pattern: cspAccountValue ? pattern(/^[0-9]{8,10}$/) : false,
    },
    gdprConsent: {
      required,
    },
    captcha: {
      required,
    },
  };

  const updateForm = (field, value) => {
    if (field === 'companyCity') {
      setValue('companyCity', value?.selectedItem?.name, { shouldValidate: true });
      return;
    }

    if (field === 'companyCounty') {
      setValue('companyCounty', value?.selectedItem?.erpCountyId, { shouldValidate: true });
      setCompanyCities(value?.id ? [] : null);
      setValue('companyCity', null);
      return;
    }

    if (field === 'companyCountry') {
      setValue('companyCountry', value?.selectedItem?.id, { shouldValidate: true });
      setCompanyCounties(value?.selectedItem?.erpCountyId ? [] : null);
      setValue('companyCounty', null, { shouldValidate: true });
      setValue('companyCity', null, { shouldValidate: true });
      return;
    }

    setValue(field, value, { shouldValidate: true });
  };

  const handleOnChangeCaptcha = isValid => {
    setValue('captcha', isValid, { shouldValidate: true });
  };

  useEffect(() => {
    setCompanyCountries(null);
    getCountries().then(data => {
      if (data) {
        setCompanyCountries(data.map(i => ({ ...i, label: i.name })));
      }
    });
    setUserData({ language: user?.language });
  }, [user?.language]);

  useEffect(() => {
    if (companyCountryValue && companyCountryValue === ROMANIA_ID) {
      getCounties(`${ API_COUNTRIES }/${ companyCountryValue }/${ COUNTIES }`).then(data => {
        if (data) {
          setCompanyCounties(data.map(i => ({ ...i, label: i.name })));
        }
      });
    }
  }, [companyCountryValue]);

  useEffect(() => {
    if (companyCountyValue) {
      getCities(`${ API_COUNTRIES }/${ companyCountryValue }/${ COUNTIES }/${ companyCountyValue }/${ CITIES }`).then(data => {
        if (data) {
          setCompanyCities(data.map(i => ({ ...i, label: i.name })));
        }
      });
    }
  }, [companyCountyValue]);

  const getCountries = useCallback(
    debounce(() => fetch(`${API_COUNTRIES}?europe_only=1`, { headers: new Headers({ 'Accept-Language': user?.language }), }), 250, { leading: true, trailing: false }),
    [user?.language]
  );

  const getCounties = useCallback(
    debounce(fetch, 250, { leading: true, trailing: false }),
    []
  );

  const getCities = useCallback(
    debounce(fetch, 250, { leading: true, trailing: false }),
    []
  );

  const handleSubmit = async () => {
    const isValid = await trigger();

    if (!isValid) {
      toast.addNotification({
        type: 'warning',
        title: t('order.formIncomplete'),
      });
      return false;
    }

    try {
      const captchaToken = await recaptchaRef.current.executeAsync();
      updateForm('captcha', captchaToken);

      if (!captchaToken) {
        toast.addNotification({
          type: 'warning',
          title: t('errors.captchaFailed'),
        });
        return true;
      }
    } catch (error) {
      toast.addNotification({
        type: 'warning',
        title: t('errors.captchaFailed'),
      });
      return true;
    }

    setLoading(true);
    setErrors([]);

    const formData = getValues();
    const payload = formData.companyCountry === ROMANIA_ID
      ? omit(formData, ['companyCountyText', 'companyCityText'])
      : omit({ ...formData, companyCounty: formData.companyCountyText, companyCity: formData.companyCityText }, ['companyCountyText', 'companyCityText']);

    try {
      const response = await fetch(REGISTRATION, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: new Headers({ 'Accept-Language': user?.language })
      });

      if (response) {
        setErrors([{ message: t('profile.registrationSuccess'), type: 'success' }]);
        reset(defaultValues);
      }
    } catch (err) {
      toast.addNotification({
        type: 'warning',
        title: err.data,
      });
      setErrors([{ message: t('profile.registrationFail'), type: 'error' }]);
    } finally {
      setLoading(false);
    }

    return true;
  };

  const onSubmit = async () => {
    const canScrollToTop = await handleSubmit();

    if (canScrollToTop) {
      modalRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <RegistrationModalComponent
      updateForm={ updateForm }
      handleOnChangeCaptcha={ handleOnChangeCaptcha }
      onSubmit={ onSubmit }
      loadingCountries={ !companyCountries }
      loadingCounties={ Array.isArray(companyCounties) && companyCounties?.length === 0 }
      loadingCities={ Array.isArray(companyCities) && companyCities?.length === 0 }
      companyCountries={ companyCountries }
      companyCounties={ companyCounties }
      companyCities={ companyCities }
      loading={ loading }
      errors={ errors }
      romaniaId={ ROMANIA_ID }
      languages={ languages }
      language={ languages.find(lang => lang.value === user?.language) }
      setLanguage={ lang => setUserData({ language: lang.value }) }
      recaptchaRef={ recaptchaRef }
      modalRef={ modalRef }
      companyCountryValue={ companyCountryValue }
      companyCountyValue={ companyCountyValue }
      companyCityValue={ companyCityValue }
      cspAccountValue={ cspAccountValue }
      register={ register }
      validators={ validators }
      rhfErrors={ rhfErrors }
      control={ control }
    />
  );
});

RegistrationModal.displayName = 'RegistrationModal';

export default RegistrationModal;
